import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  PWA_CONTINUE_BROWSER_BTN_URL,
  PWA_CONTINUE_BROWSER_ICO_URL,
  PWA_INSTALL_APP_BTN_URL,
  PWA_INSTALL_APP_ICO_URL,
  PWA_INSTALL_MODAL_BG_URL,
  PWA_INSTALL_MODAL_LOGO_PUBLIC_PATH,
} from '../constants/pwaInstallModalAssets'

const GOLD_TITLE = '#d4af37'
const GOLD_TITLE_SHADOW =
  '0 2px 4px rgba(0,0,0,0.9), 0 0 14px rgba(212, 175, 55, 0.45), 0 0 1px rgba(255, 236, 180, 0.6)'
const GOLD_BODY = '#e0c06a'
const GOLD_ACCENT = '#ffe9a8'
const GOLD_SHADOW = '0 2px 6px rgba(0,0,0,0.85), 0 0 14px rgba(212, 175, 55, 0.25)'

const FEATURE_KEYS = ['pwa.featureHome', 'pwa.featureFullscreen', 'pwa.featureFaster'] as const

export type PwaInstallOfferPanelProps = {
  /** `aria-labelledby` target for the dialog */
  titleId?: string
  installedHint: boolean
  showInstallButton: boolean
  onInstallApp: () => void
  onContinueBrowser: () => void
}

/**
 * Framed PWA offer UI (blob art) — used on `/` gate and from profile install flow.
 */
export const PwaInstallOfferPanel: React.FC<PwaInstallOfferPanelProps> = ({
  titleId = 'pwa-install-offer-title',
  installedHint,
  showInstallButton,
  onInstallApp,
  onContinueBrowser,
}) => {
  const { t } = useTranslation()
  const publicUrl = process.env.PUBLIC_URL || ''
  const logoSrc = `${publicUrl}${PWA_INSTALL_MODAL_LOGO_PUBLIC_PATH}`

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        width: 'min(92vw, 440px)',
        flexShrink: 0,
        overflow: 'visible',
        filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.55))',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1',
          maxHeight: 'min(92vw, 92dvh)',
          overflow: 'visible',
          backgroundImage: `url(${PWA_INSTALL_MODAL_BG_URL})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '3%',
            transform: 'translate(-50%, -50%)',
            width: 'min(78%, 248px)',
            maxWidth: 'none',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        >
          <img
            src={logoSrc}
            alt="Eclipsera"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              objectFit: 'contain',
              filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.65))',
            }}
            draggable={false}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            left: '7%',
            right: '7%',
            top: '23%',
            bottom: '6%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 6,
            minHeight: 0,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 2,
          }}
        >
          <h1
            id={titleId}
            style={{
              margin: 0,
              color: GOLD_TITLE,
              fontSize: 'clamp(0.9rem, 2.9vw, 1.1rem)',
              fontFamily: 'Tilt Warp, system-ui, Segoe UI, sans-serif',
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              textShadow: GOLD_TITLE_SHADOW,
            }}
          >
            {t('pwa.gateTitle')}
          </h1>
          <p
            style={{
              margin: 0,
              color: GOLD_BODY,
              fontSize: 'clamp(10px, 2.5vw, 12px)',
              lineHeight: 1.45,
              textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              flexShrink: 0,
              maxWidth: '100%',
            }}
          >
            {installedHint ? t('pwa.gateInstalledHint') : t('pwa.gateBody')}
          </p>
          {!installedHint && (
            <ul
              style={{
                margin: '2px 0 0',
                padding: 0,
                listStyle: 'none',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flexShrink: 0,
              }}
            >
              {FEATURE_KEYS.map((key) => (
                <li
                  key={key}
                  style={{
                    padding: '3px 0',
                    color: GOLD_BODY,
                    fontSize: 'clamp(9.5px, 2.4vw, 11.5px)',
                    lineHeight: 1.38,
                    textShadow: '0 1px 3px rgba(0,0,0,0.75)',
                  }}
                >
                  {t(key)}
                </li>
              ))}
            </ul>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              marginTop: 6,
              flexShrink: 0,
            }}
          >
            {showInstallButton && (
              <button
                type="button"
                onClick={onInstallApp}
                style={{
                  width: '100%',
                  minHeight: 48,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  backgroundImage: `url(${PWA_INSTALL_APP_BTN_URL})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '10px 16px',
                  color: GOLD_ACCENT,
                  fontWeight: 700,
                  fontSize: 'clamp(12px, 3vw, 15px)',
                  fontFamily: 'Tilt Warp, system-ui, sans-serif',
                  textShadow: GOLD_SHADOW,
                }}
              >
                <img
                  src={PWA_INSTALL_APP_ICO_URL}
                  alt=""
                  width={28}
                  height={28}
                  style={{ objectFit: 'contain', flexShrink: 0 }}
                  draggable={false}
                />
                {t('pwa.installApp')}
              </button>
            )}
            <button
              type="button"
              onClick={onContinueBrowser}
              style={{
                width: 'min(70%, 220px)',
                minHeight: 32,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                backgroundImage: `url(${PWA_CONTINUE_BROWSER_BTN_URL})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '5px 10px',
                color: 'rgba(230, 210, 150, 0.92)',
                fontWeight: 600,
                fontSize: 'clamp(10px, 2.5vw, 12px)',
                fontFamily: 'Tilt Warp, system-ui, sans-serif',
                textShadow: '0 1px 4px rgba(0,0,0,0.85)',
              }}
            >
              <img
                src={PWA_CONTINUE_BROWSER_ICO_URL}
                alt=""
                width={18}
                height={18}
                style={{ objectFit: 'contain', flexShrink: 0, opacity: 0.9 }}
                draggable={false}
              />
              {t('pwa.continueBrowser')}
            </button>
          </div>
          {!installedHint && (
            <p
              style={{
                margin: 0,
                fontSize: 10,
                color: 'rgba(224, 192, 106, 0.95)',
                lineHeight: 1.4,
                textShadow: '0 1px 3px rgba(0,0,0,0.75)',
                flexShrink: 0,
                padding: '0 2px',
              }}
            >
              {t('pwa.addressBarHint')}
            </p>
          )}
          <p
            style={{
              margin: 0,
              fontSize: 9,
              color: 'rgba(224, 192, 106, 0.88)',
              lineHeight: 1.4,
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0,0,0,0.75)',
              flexShrink: 0,
              padding: '0 2px',
            }}
          >
            {t('pwa.iosAddToHomeHint')}
          </p>
        </div>
      </div>
    </div>
  )
}
